import { InfoRow } from "@components/jobs2/jobs-calendar/components/calendar-container/modal/components/single-event/InfoRow";
import WorkOutlineRoundedIcon from "@mui/icons-material/WorkOutlineRounded";
import { Box, Stack, Typography } from "@mui/material";

type CostOverride = {
  by?: string;
  units?: number | string;
  value?: number | string;
  price?: number | string;
  amount?: number | string;
  currency?: string;
};

type Props = {
  name?: string | null;
  isAdvanced?: boolean;

  typeOfService?: {
    name?: string | null;
    description?: string | null;
    costBias?: number | string | null;
    baseRatePerHour?: number | string | null;
  } | null;

  serviceDescriptionOverride?: string | null;
  serviceNameOverride?: string | null;
  costOverride?: CostOverride | string | null;
  frequencyType?: string | null;
  squareFootage?: string | number | null;
};

const MEASURE_LABELS: Record<string, string> = {
  sqft: "Sq ft",
  sqm: "Sqm",
  minutes: "Minutes",
  hours: "Hours",
  days: "Days",
  weeks: "Weeks",
  months: "Months",
  per_item: "Items",
  per_square_foot: "Sq ft",
  per_car_space: "Car spaces",
  per_floor: "Floors",
  monthly: "Month",
  yearly: "Year",
  unit: "Units",
  unique: "One-time",
};

const formatFrequency = (value?: string | null) => {
  if (!value) return "—";

  return value
    .replace(/^by_/, "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

const formatCurrency = (value: number, currency: string) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
  }).format(value);

export const ScopeOfWorkTypeOfService = ({
  name,
  isAdvanced = false,
  typeOfService,
  serviceDescriptionOverride,
  serviceNameOverride,
  costOverride,
  frequencyType,
  squareFootage,
}: Props) => {
  const originalServiceName = typeOfService?.name?.trim() || name?.trim() || "Service";

  const serviceAlias = serviceNameOverride?.trim() || null;

  const serviceTypeDescription = typeOfService?.description?.trim() || null;

  const jobDescription = serviceDescriptionOverride?.trim() || null;

  const hasCustomServiceAlias = Boolean(serviceAlias);
  let parsedCostOverride: CostOverride | null = null;
  if (typeof costOverride === "string") {
    try {
      parsedCostOverride = JSON.parse(costOverride);
    } catch {
      parsedCostOverride = null;
    }
  } else {
    parsedCostOverride = costOverride ?? null;
  }

  // Lectura de precios estándar
  const units = Number(parsedCostOverride?.units ?? 0);
  const unitPrice = Number(parsedCostOverride?.price ?? parsedCostOverride?.value ?? parsedCostOverride?.amount ?? 0);
  const currency = parsedCostOverride?.currency ?? "USD";
  const measure = parsedCostOverride?.by ?? "unit";
  const normalizedMeasure = measure.toLowerCase();
  const measureLabel = MEASURE_LABELS[normalizedMeasure] ?? formatFrequency(normalizedMeasure);

  // Cálculo del total
  const total = units > 0 ? units * unitPrice : unitPrice;

  const hasServiceData = Boolean(name) || Boolean(typeOfService) || Boolean(serviceAlias);
  if (!hasServiceData) return null;

  return (
    <Box sx={CARD_STYLES.root}>
      <Stack direction="row" spacing={1.5} alignItems="flex-start">
        <Box sx={CARD_STYLES.iconBox}>
          <WorkOutlineRoundedIcon sx={{ fontSize: 20 }} />
        </Box>

        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography sx={CARD_STYLES.sectionLabel}>Service</Typography>

          <Typography
            sx={{
              mt: 0.5,
              fontSize: "1rem",
              fontWeight: 800,
              color: "text.primary",
              lineHeight: 1.25,
              overflowWrap: "anywhere",
            }}
          >
            {serviceAlias || originalServiceName}
          </Typography>

          {!hasCustomServiceAlias && serviceTypeDescription ? (
            <Box sx={{ mt: 1.5 }}>
              <Typography sx={CARD_STYLES.descriptionLabel}>Service description</Typography>

              <Typography sx={CARD_STYLES.descriptionText}>{serviceTypeDescription}</Typography>
            </Box>
          ) : null}

          {jobDescription ? (
            <Box sx={{ mt: 1.5 }}>
              <Typography sx={CARD_STYLES.descriptionLabel}>Job description</Typography>

              <Typography sx={CARD_STYLES.descriptionText}>{jobDescription}</Typography>
            </Box>
          ) : null}

          {/* ================= VISTA MODO ADVANCED / BASIC ================= */}
          {isAdvanced ? (
            <Box
              sx={{
                mt: 1.75,
                pt: 1.25,
                borderTop: "1px solid",
                borderColor: "divider",
              }}
            >
              {squareFootage ? <ServiceDetailRow label="Square footage" value={String(squareFootage)} /> : null}

              {frequencyType ? <ServiceDetailRow label="Frequency" value={formatFrequency(frequencyType)} /> : null}
              {jobDescription ? (
                <Box sx={{ mt: 1.5 }}>
                  <Typography sx={CARD_STYLES.descriptionLabel}>Job description</Typography>

                  <Typography sx={CARD_STYLES.descriptionText}>{jobDescription}</Typography>
                </Box>
              ) : null}
              {parsedCostOverride?.by ? <ServiceDetailRow label="Pricing type" value={measureLabel} /> : null}

              {units > 0 ? <ServiceDetailRow label={`Quantity (${measureLabel})`} value={String(units)} /> : null}

              {unitPrice > 0 ? <ServiceDetailRow label={`Cost per ${measureLabel}`} value={formatCurrency(unitPrice, currency)} /> : null}

              {total > 0 ? <ServiceDetailRow label="Total" value={formatCurrency(total, currency)} emphasized /> : null}
            </Box>
          ) : units > 0 || unitPrice > 0 || total > 0 ? (
            <Box
              sx={{
                mt: 1.75,
                pt: 1.25,
                borderTop: "1px solid",
                borderColor: "divider",
              }}
            >
              {units > 0 ? <ServiceDetailRow label="Unit" value={String(units)} /> : null}

              {unitPrice > 0 ? <ServiceDetailRow label="Cost per unit" value={formatCurrency(unitPrice, currency)} /> : null}

              {total > 0 ? <ServiceDetailRow label="Total" value={formatCurrency(total, currency)} emphasized /> : null}
            </Box>
          ) : null}
        </Box>
      </Stack>
    </Box>
  );
};

const ServiceDetailRow = ({ label, value, emphasized = false }: { label: string; value: string; emphasized?: boolean }) => (
  <Stack direction="row" justifyContent="space-between" alignItems="flex-start" gap={2} sx={{ mt: 0.75 }}>
    <Typography
      sx={{
        fontSize: "0.82rem",
        color: emphasized ? "text.primary" : "text.secondary",
        fontWeight: emphasized ? 700 : 400,
      }}
    >
      {label}
    </Typography>

    <Typography
      sx={{
        fontSize: emphasized ? "0.9rem" : "0.82rem",
        fontWeight: emphasized ? 800 : 700,
        color: emphasized ? "primary.main" : "text.primary",
        textAlign: "right",
        whiteSpace: "nowrap",
      }}
    >
      {value}
    </Typography>
  </Stack>
);

export const CARD_STYLES = {
  root: {
    px: { xs: 2, sm: 2.5 },
    py: { xs: 1.75, sm: 2 },
    borderRadius: 3,
    mb: 2,
    bgcolor: "background.paper",
    boxShadow: "inset 0 0 0 1px rgba(0,0,0,0.06)",
  },
  sectionLabel: {
    color: "text.secondary",
    fontSize: "0.75rem",
    fontWeight: 800,
    textTransform: "uppercase",
    letterSpacing: "0.06em",
  },
  descriptionLabel: {
    color: "text.secondary",
    fontSize: "0.7rem",
    fontWeight: 800,
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    mb: 0.35,
  },
  descriptionText: {
    fontSize: "0.875rem",
    color: "text.secondary",
    lineHeight: 1.55,
    whiteSpace: "pre-wrap",
    overflowWrap: "anywhere",
    wordBreak: "break-word",
  },
  iconBox: {
    width: 42,
    height: 42,
    borderRadius: 2.5,
    bgcolor: "rgba(25, 118, 210, 0.08)",
    color: "primary.main",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
} as const;
